<template>
  <div :class="['template-search-panel__root', showSkeletonLoading && 'loading-data']">
    <template v-if="showSkeletonLoading">
      <template-search-panel-skeleton></template-search-panel-skeleton>
    </template>

    <div v-if="hotSearchData.length" class="hot-search">
      <div class="hot-search-title">热门搜索</div>
      <div class="hot-search-keys">
        <span
          v-for="(item, index) in hotSearchData"
          :key="item + index"
          class="hot-search-key"
          @click="onKeyClick(item, 'hot')"
          >{{ item }}</span
        >
      </div>
    </div>
    <div v-if="searchHistoryData.length" class="search-history">
      <div class="search-history-title">搜索历史</div>
      <div class="empty" @click="onEmptyClick">清空</div>
      <div class="search-history-keys">
        <span
          v-for="(item, index) in searchHistoryData"
          :key="item + index"
          class="search-history-key"
          @click="onKeyClick(item, 'history')"
          >{{ item }}</span
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  apiGetBannerHot_search,
  apiGetBannerUserHot_searchHistory,
  apiPostBannerUserHot_searchDelete,
} from '@vas2t-lowcode/api';
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { getMyAppEmitter, MY_APP_EMITTER } from '../my-app/my-app.tools';
// import TemplateSearchPanelSkeleton from './template-search-panel-skeleton.vue';
import { dwOnTemplateTypeHistorySearchClick, dwOnTemplateTypeHotSearchClick } from './template-type-dw';
import { getTemplateTypeEmitter } from './template-type.tools';

defineProps({
  /** 面板显示状态 */
  isSearchPanelShow: {
    type: Boolean,
    default: false,
  },
});

/** 数据是否初始化完成 */
const isDataInit = ref(false);
/** 数据请求中 */
const isLoadingHotSearchData = ref(false);
/** 热搜词数据 */
const hotSearchData = ref<string[]>([]);

const getHotSearch = () => {
  if (isLoadingHotSearchData.value) {
    return;
  }

  isLoadingHotSearchData.value = true;
  return apiGetBannerHot_search()
    .then(({ data }) => {
      hotSearchData.value = data;
    })
    .finally(() => {
      isLoadingHotSearchData.value = false;
    });
};

/** 数据请求中 */
const isLoadingSearchHistoryData = ref(false);
/** 搜索历史数据 */
const searchHistoryData = ref<string[]>([]);
/** 获取搜索历史数据 */
const getSearchHistory = () => {
  if (isLoadingSearchHistoryData.value) {
    return;
  }
  isLoadingSearchHistoryData.value = true;
  // 不显示错误，解决未登录状态下显示登录提示
  return apiGetBannerUserHot_searchHistory(undefined)
    .then(({ data }) => {
      searchHistoryData.value = data;
    })
    .finally(() => {
      isLoadingSearchHistoryData.value = false;
    });
};

/** 清空搜索历史 */
const emptySearchHistory = () => {
  return apiPostBannerUserHot_searchDelete().then(() => {
    searchHistoryData.value.splice(0);
  });
};

const onEmptyClick = () => {
  emptySearchHistory();
};

const initData = () => {
  return Promise.race([getSearchHistory(), getHotSearch()]).finally(() => {
    isDataInit.value = true;
  });
};

const onKeyClick = (key: string, type: 'hot' | 'history') => {
  triggerSearch(key);

  if (type === 'hot') {
    dwOnTemplateTypeHotSearchClick(key);
  } else {
    dwOnTemplateTypeHistorySearchClick(key);
  }
};

const emit = defineEmits<{
  (e: 'hide-search-panel'): void;
  (e: 'trigger-search', data: string): void;
}>();

const triggerSearch = (val: string) => {
  if (!val) {
    return;
  }

  emit('trigger-search', val);

  getTemplateTypeEmitter().emit('on-app-search-by-key', val || '');
  emit('hide-search-panel');
};

const templateSearchPanelSkeleton = defineAsyncComponent(() => import('./template-search-panel-skeleton.vue'));

const isSkeletonLoading = ref(true);
let skeletonTimer = 0;
const initSkeleton = () => {
  skeletonTimer = window.setTimeout(() => {
    isSkeletonLoading.value = false;
  }, 250);
};
const showSkeletonLoading = computed(() => {
  return isLoadingHotSearchData.value || isLoadingSearchHistoryData.value || isSkeletonLoading.value;
});
onBeforeUnmount(() => {
  window.clearTimeout(skeletonTimer);
});

const bindEvent = () => {
  const emitter = getMyAppEmitter(MY_APP_EMITTER);

  emitter.on('on-reload-app-search-history', getSearchHistory);
};

onMounted(() => {
  initData();
  initSkeleton();
  bindEvent();
});
</script>

<style lang="scss" scoped>
//
.template-search-panel {
  &__root {
    &.loading-data {
      height: 208px;
    }
  }
}
</style>
